import React, { useState, useEffect } from 'react';
import { db, collection, query, where, getDocs, Timestamp, orderBy, doc, getDoc } from '../config/firebase';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { Navigate, useNavigate, useLocation } from 'react-router-dom';
import { format, isPast } from 'date-fns';
import { BookingStatus, PaymentStatus, Event } from '../types/models';
import LoadingSpinner from '../components/LoadingSpinner';
import { motion, AnimatePresence } from 'framer-motion';
import { FiCalendar, FiMapPin, FiUsers, FiClock, FiAlertCircle, FiImage } from 'react-icons/fi';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Separator } from '../components/ui/separator';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { bookingService } from '../services/bookingService';
import { getEventImages } from '../utils/getEventImages';
import confetti from 'canvas-confetti';
import { safeFormatDate } from '../utils/dateUtils';

interface Booking {
  id: string;
  eventId: string;
  eventTitle: string;
  eventDate: any;
  eventLocation?: string;
  userId: string;
  ticketTierId: string;
  ticketTierName?: string;
  quantity: number;
  totalPrice: number;
  status: BookingStatus;
  paymentStatus: PaymentStatus;
  createdAt: any;
  updatedAt?: any;
  attendeeInfo: {
    name: string;
    email: string;
    phone?: string;
  }[];
  paymentDetails?: {
    method: string;
    transactionId: string;
    amount: number;
    currency: string;
    paidAt: any;
  };
  eventImage?: string;
}

const BookingStatusBadge: React.FC<{ status: BookingStatus }> = ({ status }) => {
  const getStatusColor = () => {
    switch (status) {
      case 'confirmed':
        return 'bg-green-100 text-green-800';
      case 'cancelled':
        return 'bg-red-100 text-red-800';
      case 'refunded':
        return 'bg-amber-100 text-amber-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <Badge variant="outline" className={`${getStatusColor()} border-0`}>
      {status.charAt(0).toUpperCase() + status.slice(1)}
    </Badge>
  );
};

const PaymentStatusBadge: React.FC<{ status: PaymentStatus }> = ({ status }) => {
  const getStatusColor = () => {
    switch (status) {
      case 'paid':
        return 'bg-green-100 text-green-800';
      case 'failed':
        return 'bg-red-100 text-red-800';
      case 'refunded':
        return 'bg-amber-100 text-amber-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <Badge variant="outline" className={`${getStatusColor()} border-0`}>
      {status.charAt(0).toUpperCase() + status.slice(1)}
    </Badge>
  );
};

const BookingCard: React.FC<{ 
  booking: Booking, 
  onCancelBooking: (bookingId: string) => void 
}> = ({ booking, onCancelBooking }) => {
  const navigate = useNavigate();
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  
  const eventDate = booking.eventDate 
    ? safeFormatDate(booking.eventDate, 'PPP') 
    : 'Date not available';
    
  const isEventPast = booking.eventDate && isPast(
    booking.eventDate instanceof Timestamp 
      ? booking.eventDate.toDate() 
      : new Date(booking.eventDate)
  );
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      animate={{ opacity: 1, y: 0 }}
      whileHover={{ y: -8, boxShadow: '0 8px 32px 0 rgba(60, 132, 206, 0.25), 0 1.5px 8px 0 rgba(0,0,0,0.10)' }}
      transition={{ duration: 0.5, type: 'spring', stiffness: 80 }}
    >
      <Card className="mb-10 rounded-3xl border-0 bg-white/60 dark:bg-slate-900/60 shadow-2xl hover:shadow-[0_8px_32px_0_rgba(60,132,206,0.25),0_1.5px_8px_0_rgba(0,0,0,0.10)] transition-all duration-500 group overflow-hidden p-0 backdrop-blur-xl">
        <div className="flex flex-col md:flex-row items-stretch">
          <div className="relative w-full md:w-2/5 h-60 md:h-auto min-h-[240px] flex-shrink-0 overflow-hidden">
            <img
              src={booking.eventImage || '/images/event-placeholder.jpg'}
              alt={booking.eventTitle || 'Event image'}
              className="w-full h-full object-cover object-center transition-all duration-700 block scale-105 group-hover:scale-110"
              onError={e => {
                e.currentTarget.src = '/images/event-placeholder.jpg';
              }}
            />
            <div className="absolute inset-0 z-10 pointer-events-none rounded-tl-3xl rounded-bl-3xl animate-gradient-x bg-[linear-gradient(120deg,#3b82f6_0%,#a78bfa_50%,#f472b6_100%)] opacity-40 mix-blend-multiply" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent pointer-events-none rounded-tl-3xl rounded-bl-3xl" />
            <div className="absolute left-0 bottom-0 p-5 z-20">
              <div className="backdrop-blur-md bg-white/30 dark:bg-slate-900/30 rounded-xl px-4 py-2 flex items-center gap-2 shadow-lg">
                <h2 className="text-2xl font-extrabold text-white drop-shadow-lg truncate max-w-[80vw] md:max-w-[260px] tracking-tight">
                  {booking.eventTitle || 'Event not available'}
                </h2>
              </div>
            </div>
            <div className="absolute right-0 top-0 flex gap-2 p-5 z-20">
              <div className="backdrop-blur-md bg-white/40 dark:bg-slate-900/40 rounded-full px-3 py-1 flex items-center gap-2 shadow">
                <BookingStatusBadge status={booking.status} />
                <PaymentStatusBadge status={booking.paymentStatus} />
              </div>
            </div>
            {isEventPast && (
              <div className="absolute inset-0 flex items-center justify-center z-30">
                <Badge className="text-lg px-6 py-2 bg-white/90 text-black font-bold shadow-2xl rounded-full">PAST EVENT</Badge>
              </div>
            )}
          </div>
          
          <div className="flex-1 min-w-0 w-full p-8 flex flex-col justify-between bg-white/60 dark:bg-slate-900/60 backdrop-blur-xl">
            <div className="space-y-4">
               <div className="flex flex-wrap gap-8 text-lg text-gray-800 dark:text-gray-200 font-semibold mb-2">
                <div className="flex items-center gap-2">
                  <FiCalendar className="text-primary bg-gradient-to-tr from-blue-500 via-purple-400 to-pink-400 text-transparent bg-clip-text" />
                  <span>{eventDate}</span>
                </div>
                <div className="flex items-center gap-2">
                  <FiMapPin className="text-primary bg-gradient-to-tr from-blue-500 via-purple-400 to-pink-400 text-transparent bg-clip-text" />
                  <span>{booking.eventLocation || 'Location not available'}</span>
                </div>
              </div>
              <div className="flex flex-wrap gap-8 text-lg text-gray-700 dark:text-gray-300 mb-2">
                 <div className="flex items-center gap-2">
                  <FiUsers className="text-primary bg-gradient-to-tr from-blue-500 via-purple-400 to-pink-400 text-transparent bg-clip-text" />
                  <span>Tickets: {booking.quantity}x {booking.ticketTierName || 'Standard'}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="font-bold">Total paid:</span>
                  <span className="font-extrabold text-gray-900 dark:text-white text-2xl">${(booking.totalPrice ?? 0).toFixed(2)}</span>
                </div>
              </div>
              {booking.attendeeInfo && booking.attendeeInfo.length > 0 && (
                <div className="mt-2">
                  <h4 className="text-xs font-semibold text-gray-500 mb-1">Attendees</h4>
                  <div className="flex flex-wrap gap-2">
                    {booking.attendeeInfo.map((attendee, index) => (
                      <span key={index} className="bg-slate-100 dark:bg-slate-800 rounded px-2 py-1 text-xs text-gray-700 dark:text-gray-200">
                        {attendee.name} ({attendee.email})
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <div className="flex gap-4 items-center mt-8">
              <Button 
                variant="default" 
                size="lg"
                className="rounded-full px-8 py-3 font-bold shadow-lg bg-gradient-to-tr from-blue-500 via-purple-400 to-pink-400 text-white border-0 hover:from-pink-400 hover:to-blue-500 transition-all duration-300 text-lg"
                onClick={() => navigate(`/events/${booking.eventId}`)}
                disabled={!booking.eventTitle || booking.eventTitle === 'Event Not Found'}
              >
                View Event
              </Button>
              {!isEventPast && booking.status !== 'cancelled' && (
                <Button 
                  variant="outline" 
                  size="lg"
                  className="rounded-full px-8 py-3 font-bold border-2 border-red-300 text-red-600 hover:bg-red-100 hover:border-red-500 transition-all duration-300 text-lg bg-white/80 dark:bg-slate-900/80"
                  onClick={() => setShowCancelDialog(true)}
                >
                  Cancel Booking
                </Button>
              )}
            </div>
          </div>
        </div>

        <Dialog open={showCancelDialog} onOpenChange={setShowCancelDialog}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <FiAlertCircle className="text-red-500" />
                Cancel Booking
              </DialogTitle>
              <DialogDescription>
                Are you sure you want to cancel your booking for "{booking.eventTitle}"? This action cannot be undone.
              </DialogDescription>
            </DialogHeader>
             <div className="space-y-4 py-4">
               <div className="bg-amber-50 rounded-md p-4 text-amber-800 text-sm">
                 <p>Depending on the event's cancellation policy, you may not receive a full refund.</p>
               </div>
               <div className="space-y-2">
                 <p className="text-sm"><strong>Event:</strong> {booking.eventTitle}</p>
                 <p className="text-sm"><strong>Date:</strong> {eventDate}</p>
                 <p className="text-sm"><strong>Tickets:</strong> {booking.quantity}x {booking.ticketTierName || 'Standard'}</p>
                 <p className="text-sm"><strong>Total:</strong> ${(booking.totalPrice ?? 0).toFixed(2)}</p>
               </div>
             </div>
            <DialogFooter className="flex gap-2 sm:justify-between">
               <Button 
                variant="outline"
                onClick={() => setShowCancelDialog(false)}
              >
                Keep Booking
              </Button>
              <Button
                variant="destructive"
                onClick={() => {
                  onCancelBooking(booking.id);
                  setShowCancelDialog(false);
                }}
              >
                Cancel Booking
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </Card>
    </motion.div>
  );
};

const Bookings: React.FC = () => {
  const { user } = useAuth();
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [unlinkedBookings, setUnlinkedBookings] = useState<Booking[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'all' | 'upcoming' | 'past' | 'cancelled'>('all');
  const navigate = useNavigate();
  const location = useLocation();
  const [showCelebration, setShowCelebration] = useState(
    location.state && location.state.justBooked ? true : false
  );

  useEffect(() => {
    if (showCelebration) {
      confetti({
        particleCount: 120,
        spread: 90,
        origin: { y: 0.7 },
        scalar: 1.2
      });
      const timer = setTimeout(() => setShowCelebration(false), 4000);
      return () => clearTimeout(timer);
    }
  }, [showCelebration]);

  useEffect(() => {
    const fetchBookings = async () => {
      if (!user) {
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);
      try {
        const bookingsRef = collection(db, 'bookings');
        const q = query(
          bookingsRef,
          where('userId', '==', user.uid),
          orderBy('createdAt', 'desc')
        );

        const querySnapshot = await getDocs(q);
        const bookingsData = querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        })) as Booking[];

        const linkedBookings: Booking[] = [];
        const brokenBookings: Booking[] = [];

        for (const booking of bookingsData) {
          try {
            const eventDoc = await getDoc(doc(db, 'events', booking.eventId));
            if (eventDoc.exists()) {
              const eventData = eventDoc.data() as Event;
              const images = getEventImages(eventData);
              linkedBookings.push({
                ...booking,
                eventTitle: eventData.title,
                eventDate: eventData.startDateTime,
                eventLocation: eventData.location?.address || eventData.location?.venue || 'Online',
                eventImage: images[0] || '/images/event-placeholder.jpg',
              });
            } else {
              console.warn(`Event not found for booking ID: ${booking.id}, Event ID: ${booking.eventId}`);
              brokenBookings.push(booking);
            }
          } catch (error) {
            console.error(`Error fetching event for booking ID: ${booking.id}`, error);
            brokenBookings.push(booking);
          }
        }
        
        setBookings(linkedBookings);
        setUnlinkedBookings(brokenBookings);

      } catch (err) {
        console.error('Error fetching bookings:', err);
        setError('Failed to load bookings. Please try again later.');
        toast.error('Failed to load bookings');
      } finally {
        setLoading(false);
      }
    };

    fetchBookings();
  }, [user]);

  const handleCancelBooking = async (bookingId: string) => {
    try {
      await bookingService.updateBookingStatus(bookingId, 'cancelled');

      setBookings(prevBookings =>
        prevBookings.map(booking =>
          booking.id === bookingId
            ? { ...booking, status: 'cancelled' as BookingStatus }
            : booking
        )
      );

      toast.success('Booking cancelled successfully');
    } catch (error) {
      console.error('Error cancelling booking:', error);
      toast.error('Failed to cancel booking');
    }
  };

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[400px]">
        <LoadingSpinner />
      </div>
    );
  }

  const filteredBookings = bookings.filter(booking => {
    if (activeTab === 'all') return true;
    if (activeTab === 'cancelled') return booking.status === 'cancelled';

    let eventDate;
    try {
      if (booking.eventDate instanceof Timestamp) {
        eventDate = booking.eventDate.toDate();
      } else if (typeof booking.eventDate === 'string') {
        eventDate = new Date(booking.eventDate);
      } else {
        eventDate = new Date(booking.eventDate);
      }
      if (isNaN(eventDate.getTime())) throw new Error('Invalid date');
    } catch {
      return activeTab === 'upcoming';
    }

    const isPastEvent = isPast(eventDate);
    if (activeTab === 'past') return isPastEvent && booking.status !== 'cancelled';
    if (activeTab === 'upcoming') return !isPastEvent && booking.status !== 'cancelled';

    return true;
  });

  return (
    <div className="container mx-auto py-8 px-4">
      {showCelebration && (
        <div className="mb-8 animate-bounce-in text-center">
          <div className="text-4xl mb-2">🎉🥳🎟️</div>
          <h2 className="text-2xl font-bold text-primary mb-1">Booking Success!</h2>
          <p className="text-lg text-gray-700">You just booked your ticket! Get ready for an awesome event! 🦄✨</p>
       </div>
      )}
      <div className="mb-8">
        <h1 className="text-3xl font-bold">My Bookings</h1>
        <p className="text-gray-600 mt-2">Manage your event tickets and bookings</p>
      </div>

      <Tabs defaultValue="all" value={activeTab} onValueChange={(value) => setActiveTab(value as 'all' | 'upcoming' | 'past' | 'cancelled')}>
        <TabsList className="mb-6">
          <TabsTrigger value="all">All</TabsTrigger>
          <TabsTrigger value="upcoming">Upcoming Events</TabsTrigger>
          <TabsTrigger value="past">Past Events</TabsTrigger>
          <TabsTrigger value="cancelled">Cancelled Events</TabsTrigger>
        </TabsList>

        <AnimatePresence mode="wait">
          <motion.div
            key={activeTab}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.2 }}
          >
            {filteredBookings.length === 0 ? (
              <div className="text-center py-12 bg-white rounded-lg shadow-sm border">
                <FiCalendar className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-lg font-medium text-gray-900">No {activeTab} bookings</h3>
                <p className="mt-1 text-gray-500">You don't have any events in this category.</p>
                <Button className="mt-6" onClick={() => navigate('/events')}>
                  Explore Events
                </Button>
              </div>
            ) : (
              <div className="space-y-6">
                {filteredBookings.map(booking => (
                  <BookingCard
                    key={booking.id}
                    booking={booking}
                    onCancelBooking={handleCancelBooking}
                  />
                ))}
              </div>
            )}
          </motion.div>
        </AnimatePresence>
      </Tabs>
      
      {unlinkedBookings.length > 0 && (
        <div className="mt-12">
            <h2 className="text-2xl font-bold text-amber-600 mb-4">Bookings with Missing Events</h2>
            <p className="mb-4 text-gray-600">The following bookings could not be fully loaded because the associated event has been deleted. You can still manage the booking if needed.</p>
            <div className="space-y-4">
                {unlinkedBookings.map(booking => (
                    <Card key={booking.id} className="bg-amber-50 border-amber-200">
                        <CardHeader>
                            <CardTitle className="text-amber-800">Booking ID: {booking.id}</CardTitle>
                            <CardDescription>This booking is for a deleted event (Event ID: {booking.eventId}).</CardDescription>
                        </CardHeader>
                        <CardContent>
                            <p>Booked on: {safeFormatDate(booking.createdAt, 'PPP')}</p>
                            <p>Tickets: {booking.quantity}</p>
                        </CardContent>
                        <CardFooter>
                           <Button variant="destructive" onClick={() => {
                               // Here you could add logic to delete or archive these bookings
                               toast.info("Deletion/archiving for unlinked bookings is not yet implemented.");
                           }}>
                               Manage Booking
                           </Button>
                        </CardFooter>
                    </Card>
                ))}
            </div>
        </div>
      )}
    </div>
  );
};

export default Bookings; 